/**
 * Founding Explorer Badge Component
 * Displays the exclusive gold badge for Founding 200 members
 */
import { cn } from "@/lib/utils";

// Badge image URL
const BADGE_IMAGE = "/founding-explorer-badge.png";

// Badge sizes
const SIZES = {
  xs: "h-6 w-6",
  sm: "h-8 w-8",
  md: "h-12 w-12",
  lg: "h-16 w-16",
  xl: "h-24 w-24",
};

export const FoundingBadge = ({ 
  size = "sm", 
  showTooltip = true, 
  founderNumber,
  className 
}) => {
  return (
    <img
      src={BADGE_IMAGE}
      alt={`Founding Explorer #${founderNumber || ''} of 200`}
      title={showTooltip ? `Founding Explorer #${founderNumber || ''} of 200` : undefined}
      className={cn(
        "object-contain",
        "drop-shadow-[0_0_8px_rgba(251,191,36,0.8)] animate-pulse-glow",
        SIZES[size],
        className
      )}
      data-testid="founding-badge"
    />
  );
};

// Inline badge with text for profile/cards
export const FoundingBadgeInline = ({ 
  founderNumber, 
  size = "sm",
  showNumber = true,
  className 
}) => {
  return (
    <div
      className={cn(
        "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full",
        "bg-gradient-to-r from-amber-500/20 via-yellow-500/20 to-amber-500/20",
        "border border-amber-400/30",
        "shadow-[0_0_12px_rgba(251,191,36,0.5)]",
        className
      )}
      data-testid="founding-badge-inline"
    >
      <img 
        src={BADGE_IMAGE} 
        alt="Founding Explorer"
        className={cn(
          "object-contain drop-shadow-[0_0_6px_rgba(251,191,36,0.8)]",
          size === "xs" ? "h-4 w-4" : "h-5 w-5"
        )}
      />
      <span className={cn(
        "font-semibold text-amber-700 dark:text-amber-400 drop-shadow-[0_0_4px_rgba(251,191,36,0.5)]",
        size === "xs" ? "text-[10px]" : "text-xs"
      )}>
        {showNumber && founderNumber ? `#${founderNumber}/200` : "Founder"}
      </span>
    </div>
  );
};

// Full badge card for profile page
export const FoundingBadgeCard = ({ founderNumber, joinedAt }) => {
  const formattedDate = joinedAt 
    ? new Date(joinedAt).toLocaleDateString('en-US', { 
        month: 'long', 
        year: 'numeric' 
      }) 
    : null;

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-2xl p-4",
        "bg-gradient-to-br from-amber-500/10 via-yellow-500/10 to-amber-600/10",
        "border border-amber-400/30",
        "shadow-[0_0_25px_rgba(251,191,36,0.3)]"
      )}
      data-testid="founding-badge-card"
    >
      {/* Shimmer effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-400/10 to-transparent -translate-x-full animate-shimmer" />
      
      <div className="relative flex flex-col items-center text-center gap-2">
        <div className="flex-shrink-0">
          <img 
            src={BADGE_IMAGE}
            alt="Founding Explorer Badge"
            className="h-16 w-16 object-contain drop-shadow-[0_0_12px_rgba(251,191,36,0.8)] animate-pulse-glow"
          />
        </div>
        <div>
          <h3 className="font-semibold text-amber-700 dark:text-amber-400 text-lg drop-shadow-[0_0_8px_rgba(251,191,36,0.5)]">
            Founding Explorer #{founderNumber} of 200
          </h3>
          <p className="text-sm text-amber-600/80 dark:text-amber-500/80 mt-1">
            One of the original believers
          </p>
          {formattedDate && (
            <p className="text-xs text-muted-foreground mt-1">
              Member since {formattedDate}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default FoundingBadge;
